import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Checkbox, DropdownMenu, Flex, IconButton, Tooltip } from '@radix-ui/themes';
import mixpanel from 'mixpanel-browser';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Icon from '~/components/Icon';
import onClickOutside from '~/hooks/use-on-click-outside';
import { columnsControlShape } from './use-columns-control';

const StyledDropdownMenuItem = styled(DropdownMenu.Item)`
  &[data-highlighted],
  &[aria-pressed='true'] {
    background-color: var(--accent-4);
    z-index: 1;
  }
`;

const PointerCursorIcon = styled(Icon)`
  cursor: grabbing;
`;

const StyledDropdownMenuContent = styled(DropdownMenu.Content)`
  overflow: hidden;
`;

function OptionItem({ option, onOptionClick }) {
  const { key, label, selected: checked } = option;

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <StyledDropdownMenuItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      checked={checked}
      onClick={() => onOptionClick(key)}
      onSelect={e => e.preventDefault()}
      asChild
    >
      <Flex
        px='3'
        justify='between'
      >
        <Flex
          align='center'
          gap='2'
        >
          <Checkbox
            checked={checked}
            readOnly
            color='orange'
          />
          {label}
        </Flex>
        <PointerCursorIcon
          name='drag-handle-dots-2'
          width={16}
          height={16}
          inline
        />
      </Flex>
    </StyledDropdownMenuItem>
  );
}

OptionItem.propTypes = {
  option: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    selected: PropTypes.bool.isRequired,
  }).isRequired,
  onOptionClick: PropTypes.func.isRequired,
};

function DragableOptionList({ options, onOptionClick, sensors, changeColumnOptionsOrder }) {
  const [order, setOrder] = useState([]);
  const orderedOptions = order.map(key => options.find(option => option.key === key)).filter(Boolean);

  useEffect(() => {
    setOrder(options.map(option => option.key));
  }, [options]);

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over && active?.id !== over?.id) {
      changeColumnOptionsOrder(active.id, over.id);

      setOrder(prevOrder => {
        const oldIndex = prevOrder.indexOf(active.id);
        const newIndex = prevOrder.indexOf(over.id);
        const newOrder = [...prevOrder];
        // Take out the item at oldIndex
        const [movedItem] = newOrder.splice(oldIndex, 1);
        // Insert the item after newIndex
        newOrder.splice(newIndex, 0, movedItem);
        return newOrder;
      });
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={orderedOptions.map(option => option.key)}
        strategy={verticalListSortingStrategy}
      >
        <Flex
          direction='column'
          overflow='hidden'
        >
          {orderedOptions.map(option => {
            return (
              <OptionItem
                key={option.key}
                option={option}
                onOptionClick={onOptionClick}
              />
            );
          })}
        </Flex>
      </SortableContext>
    </DndContext>
  );
}

DragableOptionList.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  onOptionClick: PropTypes.func.isRequired,
  sensors: PropTypes.array.isRequired,
  changeColumnOptionsOrder: PropTypes.func.isRequired,
};

function ColumnsControl({ columnsControl, noun }) {
  const {
    onOptionClick,
    selectAllColumns,
    deselectAllColumns,
    changeColumnOptionsOrder,
    resetToDefault,
    selectableColumnsOptions,
  } = columnsControl;
  const [isOpen, setIsOpen] = useState(false);

  const contentRef = useRef(null);
  const buttonRef = useRef(null);

  const [initialShownColumnKeys, setInitialShownColumnKeys] = useState([]);
  const [initialOrderColumnKeys, setInitialOrderColumnKeys] = useState([]);

  const onOpenChange = useCallback(
    isOpen => {
      const shownColumnKeys = selectableColumnsOptions.filter(option => option.selected).map(option => option.key);
      const orderColumnKeys = selectableColumnsOptions.map(option => option.key);

      if (isOpen) {
        mixpanel.track(`View ${noun}s page custom table settings`, { isOpen });

        setInitialShownColumnKeys(shownColumnKeys);
        setInitialOrderColumnKeys(orderColumnKeys);
        return;
      }

      const columnShownChanged = JSON.stringify(shownColumnKeys) !== JSON.stringify(initialShownColumnKeys);
      const columnOrderChanged = JSON.stringify(orderColumnKeys) !== JSON.stringify(initialOrderColumnKeys);

      if (columnOrderChanged || columnShownChanged) {
        mixpanel.track(`Modify ${noun}s page custom table`, {
          [`${noun}s page custom table columns selection changed`]: shownColumnKeys,
          [`${noun}s page custom table columns list previous setting`]: initialShownColumnKeys,
          [`${noun}s page custom table has rearranged columns`]: columnOrderChanged,
          [`${noun}s page custom table has changed column selection`]: columnShownChanged,
        });
      }
    },
    [selectableColumnsOptions, initialOrderColumnKeys, initialShownColumnKeys, noun],
  );

  onClickOutside([contentRef, buttonRef], () => {
    if (isOpen) {
      onOpenChange(false);
      setIsOpen(false);
    }
  });

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Escape' && isOpen) {
        onOpenChange(false);
        setIsOpen(false);
      }
    },
    [isOpen, onOpenChange],
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  const selectedOptions = selectableColumnsOptions.filter(option => option.selected);
  const selectAllStatus = !selectedOptions.length
    ? false
    : selectedOptions.length === selectableColumnsOptions.length
      ? true
      : 'indeterminate';

  function onSelectAllClicked() {
    if (selectAllStatus === true) return deselectAllColumns();
    return selectAllColumns();
  }

  function onResetToDefault() {
    mixpanel.track(`Reset ${noun}s page custom table to default`);
    resetToDefault();
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  function onColumnsControlTriggerClick() {
    onOpenChange(!isOpen);
    setIsOpen(!isOpen);
  }

  return (
    <DropdownMenu.Root
      modal={false}
      open={isOpen}
    >
      <Tooltip content='Manage columns'>
        <Box
          ref={buttonRef}
          p='2'
          asChild
          onClick={onColumnsControlTriggerClick}
        >
          <IconButton
            color='gray'
            variant='ghost'
            size='1'
          >
            <Icon
              name='settings-02'
              width={16}
              height={16}
              inline
            />
          </IconButton>
        </Box>
      </Tooltip>
      {/* Hack, cause DropdownMenu and Tooltip trigger events conflict */}
      {/* And DropdownMenu do not work without DropdownMenu.Trigger */}
      {/* And DropdownMenu Trigger will not render without content */}
      <DropdownMenu.Trigger>
        <span />
      </DropdownMenu.Trigger>
      <StyledDropdownMenuContent
        ref={contentRef}
        align='end'
        color='gray'
        variant='soft'
      >
        <StyledDropdownMenuItem
          onSelect={e => e.preventDefault()}
          onClick={onSelectAllClicked}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onSelectAllClicked();
            }
          }}
        >
          <Checkbox
            checked={selectAllStatus}
            color='orange'
          />
          Select all
        </StyledDropdownMenuItem>
        <DropdownMenu.Separator />
        <DragableOptionList
          options={selectableColumnsOptions}
          onOptionClick={onOptionClick}
          sensors={sensors}
          changeColumnOptionsOrder={changeColumnOptionsOrder}
        />
        <DropdownMenu.Separator />
        <Button
          size='1'
          variant='ghost'
          radius='large'
          mx='1'
          onClick={onResetToDefault}
        >
          Reset to default
        </Button>
      </StyledDropdownMenuContent>
    </DropdownMenu.Root>
  );
}

ColumnsControl.propTypes = {
  columnsControl: columnsControlShape.isRequired,
  noun: PropTypes.string.isRequired,
};

export default ColumnsControl;
